"use client";

import { Button } from "@/components/ui/button";
import HoverImage from "@/components/ui/hover-image";
import { generateWebPageSchema } from "@/lib/seo";
import Link from "next/link";
import { Heart, Users, Lightbulb, Target } from "lucide-react";
import DisavantajatiCard from "@/components/DeissadvantagedCard";

const webPageSchema = generateWebPageSchema(
  "Cariere - Mindtale",
  "Alătură-te echipei Mindtale și construiește împreună cu noi o platformă de psihoterapie care face diferența. Căutăm oameni care cred în accesul real la sănătate mintală.",
  "https://mindtale.ro/cariere"
);

const values = [
  {
    icon: Heart,
    title: "Empatie ca fundație",
    description:
      "Nu construim doar software. Construim un spațiu sigur pentru oameni reali, cu povești reale. Fiecare decizie pe care o luăm pornește de la întrebarea: cum putem să fim de ajutor?",
  },
  {
    icon: Users,
    title: "Echipă mică, impact mare",
    description:
      "Suntem o echipă tânără, dar cu o misiune clară. Fiecare om contează, fiecare voce e auzită. Lucrăm transparent, flexibil și cu respect pentru ritmul fiecăruia.",
  },
  {
    icon: Lightbulb,
    title: "Învățare continuă",
    description:
      "Suntem la început de drum și învățăm constant — din greșeli, din feedback, din conversații. Dacă vrei un mediu în care să crești, nu doar să execuți, acesta e locul.",
  },
  {
    icon: Target,
    title: "Impact social real",
    description:
      "Nu e doar un job. E o oportunitate să fii parte dintr-un model care face terapia accesibilă pentru persoane care altfel nu și-ar permite. Muncă cu sens.",
  },
];

const positions: {
  title: string;
  type: string;
  redirect: string;
  description: string;
  skills: string[];
  benefits: string[];
}[] = [];

export default function CareerPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <section className="relative bg-[#FDF9F5] text-[#264653] overflow-hidden">
        {/* Background accents */}
        <div className="pointer-events-none absolute -top-24 -right-10 w-72 h-72 bg-[#E9C46A]/25 blur-3xl rounded-full" />
        <div className="pointer-events-none absolute top-1/2 -left-24 w-72 h-72 bg-[#2A9D8F]/12 blur-3xl rounded-full" />

        {/* Hero */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-20 pb-16 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E9C46A]/40 border border-[#E9C46A]/60 text-xs font-medium mb-6">
            <span>Construiește cu noi o platformă cu impact</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight mb-5">
            Cariere la Mindtale:
            <br />
            un loc pentru
            <span className="text-[#E76F51]">
              {" "}
              echilibru, dezvoltare și colaborare.
            </span>
          </h1>

          <p className="text-lg text-[#264653]/82 max-w-2xl mx-auto leading-relaxed mb-8">
            Mindtale nu e o companie ca oricare alta. E o întreprindere socială
            construită pe ideea că tehnologia și empatia pot face terapia
            accesibilă pentru toată lumea — nu doar pentru cei care își permit.
          </p>

          <div className="max-w-4xl mx-auto">
            <DisavantajatiCard variant="pozitii" />
          </div>
        </div>

        {/* Mission section */}
        <div className="bg-white border-y border-[#E9C46A]/20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 grid gap-10 lg:grid-cols-[1.5fr,1.5fr] items-center">
            <div className="space-y-5 text-left">
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#264653]">
                Ce înseamnă să lucrezi la Mindtale?
              </h2>
              <p className="text-[#264653]/80 text-base leading-relaxed">
                Înseamnă să fii parte dintr-o echipă mică, dar cu viziune mare.
                Să construiești ceva care schimbă vieți reale. Să lucrezi
                flexibil, transparent și cu oameni care cred că empatia și
                tehnologia pot coexista.
              </p>
              <p className="text-[#264653]/78 text-base leading-relaxed">
                Nu căutăm oameni perfecți. Căutăm oameni care vor să învețe, să
                contribuie și să fie parte dintr-un proiect mai mare decât ei
                înșiși.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-[#264653]/90">
                <li className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-[#DC7050]" />
                  <span>Echipă mică, colaborare strânsă, voce auzită.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-[#DC7050]" />
                  <span>
                    Lucru remote, program flexibil, respect pentru echilibru.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-[#DC7050]" />
                  <span>
                    Oportunitatea de a contribui la construirea proiectului Mindtale cu impact social real.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="bg-[#FDF9F5] py-18 lg:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#264653] mb-4">
                Valorile după care lucrăm
              </h2>
              <p className="text-[#264653]/78 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
                Nu sunt doar cuvinte frumoase pe un site. Sunt principiile care
                ne ghidează deciziile, prioritățile și modul în care ne tratăm
                unii pe alții.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {values.map((value) => {
                const IconComponent = value.icon;
                return (
                  <div
                    key={value.title}
                    className="relative bg-white/95 rounded-3xl border border-[#E9C46A]/40 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 px-6 py-6"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#DC7050]/10 text-[#E76F51] mb-4">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#264653] mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-[#264653]/80 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Open positions */}
        <div className="bg-white py-18 lg:py-20 border-t border-[#E9C46A]/30">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#264653] mb-4">
                Roluri deschise acum
              </h2>
              <p className="text-[#264653]/78 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed mb-6">
                Suntem în faza de construcție activă și căutăm oameni care vor
                să facă parte din echipa Mindtale. Dacă rezonezi cu unul din rolurile de mai jos,
                apasă pe "Aplică acum" pentru a vedea descrierea completă și a trimite aplicația.
              </p>
              <div className="bg-white/80 rounded-3xl border border-[#E9C46A]/60 px-6 py-4 max-w-2xl mx-auto">
                <p className="text-sm text-[#264653]/85 leading-relaxed">
                  <span className="font-semibold">Cum aplici?</span> Fiecare poziție are instrucțiuni complete. Aplicațiile se trimit prin email la{" "}
                  <a
                    href="mailto:hello@mindtale.ro"
                    className="font-bold text-[#E76F51] hover:text-[#d75a42] transition-colors"
                  >
                    hello@mindtale.ro
                  </a>
                </p>
              </div>
            </div>

            {positions.length === 0 && (
              <div className="text-center py-10 rounded-3xl border border-[#E9C46A]/40 bg-[#FDF9F5]">
                <p className="text-[#264653]/70 text-sm leading-relaxed">
                  Nu avem roluri deschise în acest moment, dar suntem mereu deschiși să cunoaștem oameni buni.
                  <br />Scrie-ne la{" "}
                  <a href="mailto:hello@mindtale.ro" className="font-semibold text-[#E76F51] hover:underline">
                    hello@mindtale.ro
                  </a>
                  .
                </p>
              </div>
            )}
            <div className="space-y-6">
              {positions.map((position) => (
                <div
                  key={position.title}
                  className="bg-[#FDF9F5] rounded-3xl border border-[#E9C46A]/40 shadow-sm hover:shadow-md transition-all duration-300 px-6 py-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-[#264653] mb-2">
                        {position.title}
                      </h3>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 border border-[#E9C46A]/60 text-xs font-medium text-[#264653]/90">
                        {position.type}
                      </div>
                    </div>
                    <Link href={position.redirect}>
                      <Button
                        size="sm"
                        className="bg-[#DC7050] hover:bg-[#d75a42] text-white rounded-full px-6 py-2 text-sm font-medium"
                      >
                        Aplică acum
                      </Button>
                    </Link>
                  </div>

                  <p className="text-sm text-[#264653]/80 leading-relaxed mb-4">
                    {position.description}
                  </p>

                  <div className="mb-5">
                    <h4 className="text-xs font-semibold text-[#264653] uppercase tracking-wide mb-2">
                      Ce căutăm
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {position.skills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1 bg-white/90 border border-[#2A9D8F]/30 rounded-full text-xs text-[#264653]/85"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white/60 rounded-2xl border border-[#E9C46A]/50 px-4 py-4">
                    <h4 className="text-xs font-semibold text-[#264653] uppercase tracking-wide mb-2">
                      Ce oferim
                    </h4>
                    <ul className="space-y-1.5 text-xs text-[#264653]/85">
                      {position.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-2">
                          <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-[#2A9D8F]" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-[#E9C46A]/40 py-16 text-center border-t border-[#E9C46A]/60">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#264653] mb-4">
              Nu vezi un rol potrivit, dar rezonezi cu misiunea?
            </h2>
            <p className="text-sm sm:text-base text-[#264653]/85 mb-6 leading-relaxed">
              Trimite-ne un mesaj. Suntem deschiși să cunoaștem oameni care cred
              în ce construim, chiar dacă nu avem un rol deschis exact pentru
              profilul tău.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/contact">
                <Button className="bg-[#DC7050] hover:bg-[#d75a42] text-white rounded-full px-8 py-3 text-base font-medium shadow-md hover:shadow-lg transition-all">
                  Contactează-ne
                </Button>
              </Link>
              <Link href="/despre-noi">
                <Button
                  variant="outline"
                  className="border-[#264653]/20 text-[#264653] hover:bg-white/70 rounded-full px-8 py-3 text-base font-medium"
                >
                  Află mai multe despre misiune
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
